import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Drawer, Button, Input, Tooltip, Avatar, Tag, Popconfirm, Spin, message as antMessage } from 'antd';
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  CheckCircleFilled,
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import apiService from '../../services/apiService';
import './AIChatWidget.css';

const QUICK_PROMPTS = [
  'Create a lead for John Smith, john@acme.com, 9876543210, Acme Corp',
  "What's in my pipeline?",
  'Show me all leads created this week',
  'Schedule a meeting with Sarah Kim tomorrow at 3pm for 30 min',
  'Create a task to follow up with Acme Corp by Friday',
];

// `inline` mode renders the chat shell directly (no floating button, no Drawer)
// — used for the mobile-only chat experience where the chat IS the app.
const AIChatWidget = ({ inline = false }) => {
  const [open, setOpen] = useState(inline);
  const [view, setView] = useState('chat'); // 'chat' | 'history'
  const [messages, setMessages] = useState([]); // {id, sender:'user'|'agent', text, ts, meta?}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await apiService.get('/ai-agent/sessions', { params: { limit: 25 } });
      const list = res.data?.data?.sessions || res.data?.sessions || [];
      setSessions(list);
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Failed to load chat history');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadSession = async (sid) => {
    setLoadingSession(true);
    try {
      const res = await apiService.get(`/ai-agent/session/${sid}`);
      const data = res.data?.data || res.data || {};
      const history = (data.messages || []).map((m, i) => ({
        id: `${sid}-${i}`,
        sender: m.sender === 'user' ? 'user' : 'agent',
        text: m.message || '',
        ts: m.timestamp ? new Date(m.timestamp) : new Date(),
        meta: m.metadata || {},
      }));
      setMessages(history);
      setSessionId(sid);
      setView('chat');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Could not load that chat');
    } finally {
      setLoadingSession(false);
    }
  };

  const deleteSession = async (sid) => {
    try {
      await apiService.delete(`/ai-agent/session/${sid}`);
      setSessions((s) => s.filter((x) => x.sessionId !== sid));
      if (sid === sessionId) {
        setMessages([]);
        setSessionId(null);
      }
      antMessage.success('Chat deleted');
    } catch (err) {
      antMessage.error(err.response?.data?.message || 'Could not delete chat');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setView('chat');
  };

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open && view === 'chat') {
      const t = setTimeout(() => inputRef.current?.focus?.(), 250);
      return () => clearTimeout(t);
    }
  }, [open, view]);

  // Fetch sessions whenever the user switches to history view.
  useEffect(() => {
    if (open && view === 'history') {
      fetchSessions();
    }
  }, [open, view, fetchSessions]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    const localId = Date.now();
    const userMsg = { id: `u-${localId}`, sender: 'user', text: value, ts: new Date() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await apiService.post('/ai-agent/chat', {
        message: value,
        sessionId: sessionId || undefined,
      });
      // Server shape: { success, sessionId, response, metadata }
      const body = res.data || {};
      const reply = body.response || body.message || body.data?.response || 'No response.';
      const meta = body.metadata || body.data?.metadata || {};
      const newSid = body.sessionId || body.data?.sessionId;
      if (newSid && newSid !== sessionId) setSessionId(newSid);
      setMessages((m) => [
        ...m,
        {
          id: `a-${localId}`,
          sender: 'agent',
          text: reply,
          ts: new Date(),
          meta,
          error: Boolean(meta?.error),
        },
      ]);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Could not reach the AI agent.';
      setMessages((m) => [
        ...m,
        { id: `a-err-${localId}`, sender: 'agent', text: msg, ts: new Date(), error: true },
      ]);
      antMessage.error(msg);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const renderMessageText = (text) => {
    // Lightweight rendering: preserve line breaks; render ` ``` ` blocks as
    // <pre>; render single backticks as inline code; bold **bold** segments.
    const parts = String(text || '').split(/```/);
    return parts.map((part, idx) => {
      if (idx % 2 === 1) {
        // code block
        return (
          <pre key={idx} className="ai-chat-codeblock">
            {part}
          </pre>
        );
      }
      // inline-format pass
      const inline = part.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((seg, i) => {
        if (/^\*\*[^*]+\*\*$/.test(seg)) {
          return <strong key={i}>{seg.slice(2, -2)}</strong>;
        }
        if (/^`[^`]+`$/.test(seg)) {
          return <code key={i} className="ai-chat-inline-code">{seg.slice(1, -1)}</code>;
        }
        return <span key={i}>{seg}</span>;
      });
      return (
        <span key={idx} style={{ whiteSpace: 'pre-wrap' }}>
          {inline}
        </span>
      );
    });
  };

  // The chat shell — same DOM in both inline (mobile) and Drawer modes.
  const shell = (
    <div className="ai-chat-shell" style={inline ? { height: '100vh' } : undefined}>
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header-left">
              {view === 'history' ? (
                <Tooltip title="Back to chat">
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setView('chat')}
                    className="ai-chat-icon-btn"
                  />
                </Tooltip>
              ) : (
                <div className="ai-chat-avatar-glow">
                  <RobotOutlined />
                </div>
              )}
              <div>
                <div className="ai-chat-title">
                  {view === 'history' ? 'Recent chats' : 'AI Assistant'}
                </div>
                <div className="ai-chat-subtitle">
                  {view === 'history' ? (
                    <>{sessions.length} saved chat{sessions.length === 1 ? '' : 's'}</>
                  ) : (
                    <>
                      <span className="ai-chat-status-dot" /> Online · Powered by your AI
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="ai-chat-header-actions">
              {view === 'chat' && (
                <>
                  <Tooltip title="New chat">
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={startNewChat}
                      className="ai-chat-icon-btn"
                    />
                  </Tooltip>
                  <Tooltip title="Recent chats">
                    <Button
                      type="text"
                      icon={<HistoryOutlined />}
                      onClick={() => setView('history')}
                      className="ai-chat-icon-btn"
                    />
                  </Tooltip>
                </>
              )}
              {!inline && (
                <Tooltip title="Close">
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => setOpen(false)}
                    className="ai-chat-icon-btn"
                  />
                </Tooltip>
              )}
            </div>
          </div>

          {/* History view */}
          {view === 'history' && (
            <div className="ai-chat-body">
              {sessionsLoading || loadingSession ? (
                <div className="ai-chat-history-loading">
                  <Spin />
                </div>
              ) : sessions.length === 0 ? (
                <div className="ai-chat-history-empty">
                  No saved chats yet. Send a message to start one.
                </div>
              ) : (
                <div className="ai-chat-history-list">
                  {sessions.map((s) => (
                    <div
                      key={s.sessionId}
                      className={`ai-chat-history-item ${s.sessionId === sessionId ? 'active' : ''}`}
                    >
                      <button
                        className="ai-chat-history-item-main"
                        onClick={() => loadSession(s.sessionId)}
                      >
                        <div className="ai-chat-history-item-title">{s.title || 'New Chat'}</div>
                        {s.preview && (
                          <div className="ai-chat-history-item-preview">{s.preview}</div>
                        )}
                        <div className="ai-chat-history-item-ts">
                          {new Date(s.lastMessageAt || s.createdAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </button>
                      <Popconfirm
                        title="Delete this chat?"
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteSession(s.sessionId)}
                      >
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          className="ai-chat-history-item-delete"
                          aria-label="Delete chat"
                        />
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          {view === 'chat' && (
          <div className="ai-chat-body" ref={scrollRef}>
            {messages.length === 0 && !sending && (
              <div className="ai-chat-welcome">
                <div className="ai-chat-welcome-orb">
                  <RobotOutlined />
                </div>
                <h3 className="ai-chat-welcome-title">How can I help you today?</h3>
                <p className="ai-chat-welcome-sub">
                  I can create leads, deals, tasks, meetings, and pull pipeline insights — just ask in plain English.
                </p>
                <div className="ai-chat-prompts">
                  {QUICK_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      className="ai-chat-prompt-chip"
                      onClick={() => send(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div
                key={m.id}
                className={`ai-chat-row ${m.sender === 'user' ? 'is-user' : 'is-agent'}`}
              >
                {m.sender === 'agent' && (
                  <Avatar
                    size={28}
                    icon={<RobotOutlined />}
                    className="ai-chat-msg-avatar agent"
                  />
                )}
                <div
                  className={`ai-chat-bubble ${m.sender === 'user' ? 'user' : 'agent'} ${
                    m.error ? 'error' : ''
                  }`}
                >
                  <div className="ai-chat-bubble-text">{renderMessageText(m.text)}</div>
                  {m.meta?.toolsExecuted?.length > 0 && (
                    <div className="ai-chat-tool-cards">
                      {m.meta.toolsExecuted.map((t, i) => (
                        <div key={i} className="ai-chat-tool-card">
                          <CheckCircleFilled style={{ color: '#22c55e' }} />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ai-chat-bubble-ts">
                    {m.ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                {m.sender === 'user' && (
                  <Avatar
                    size={28}
                    icon={<UserOutlined />}
                    className="ai-chat-msg-avatar user"
                  />
                )}
              </div>
            ))}

            {sending && (
              <div className="ai-chat-row is-agent">
                <Avatar size={28} icon={<RobotOutlined />} className="ai-chat-msg-avatar agent" />
                <div className="ai-chat-bubble agent typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Composer (chat view only) */}
          {view === 'chat' && (
          <div className="ai-chat-composer">
            <Input.TextArea
              ref={inputRef}
              autoSize={{ minRows: 1, maxRows: 5 }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask anything — create lead, schedule meeting, find deals…"
              className="ai-chat-textarea"
              disabled={sending}
            />
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!input.trim()}
              onClick={() => send()}
              className="ai-chat-send-btn"
              size="large"
            />
          </div>
          )}
          {view === 'chat' && sessionId && (
            <div className="ai-chat-footer">
              <Tag color="blue" bordered={false} style={{ fontSize: 10 }}>
                session · {String(sessionId).slice(-6)}
              </Tag>
              <span className="ai-chat-shortcut">Enter to send · Shift+Enter newline</span>
            </div>
          )}
        </div>
  );

  // Inline mode: the chat IS the page — no floating button, no Drawer.
  if (inline) {
    return shell;
  }

  return (
    <>
      {!open && (
        <Tooltip title="Ask the AI agent" placement="left">
          <button
            className="ai-chat-fab"
            aria-label="Open AI assistant"
            onClick={() => setOpen(true)}
          >
            <MessageOutlined style={{ fontSize: 22 }} />
          </button>
        </Tooltip>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        width={440}
        closable={false}
        styles={{ body: { padding: 0, background: '#0f172a' }, header: { display: 'none' } }}
        rootClassName="ai-chat-drawer-root"
      >
        {shell}
      </Drawer>
    </>
  );
};

export default AIChatWidget;
